import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import { z } from "zod";
import {
  FieldGroup,
  FieldGroupSchema,
  FieldGroupInstance,
  FieldGroupInstanceSchema,
  HydratedFieldGroupInstance,
  HydratedFieldGroupInstanceSchema,
  ControlRelationship,
  ControlRelationshipSchema,
  CreateFieldGroupInstanceRequest,
  UpdateFieldGroupInstanceRequest,
  CreateFieldGroupRequest,
  UpdateFieldGroupRequest,
} from "../models/audit/library";

// Hook for fetching all field groups (library types)
export const useFieldGroups = () => {
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFieldGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchFastApi({
        path: "/controls/v2/field-groups",
        method: "GET",
      });

      const data = z.array(FieldGroupSchema).parse(response.fieldGroups || []);
      setFieldGroups(data);
    } catch (error) {
      console.error("Failed to fetch field groups:", error);
      toast.error("Failed to fetch libraries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFieldGroups();
  }, [fetchFieldGroups]);

  return {
    fieldGroups,
    loading,
    refetch: fetchFieldGroups,
  };
};

// Hook for fetching field group instances with pagination and search
export const useFieldGroupInstances = (
  fieldGroupId: string | null,
  filters: {
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  } = {},
) => {
  const LIMIT = 25;

  const [instances, setInstances] = useState<FieldGroupInstance[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const isLoadingRef = useRef(false);

  // Store filters in ref to avoid recreating fetchInstances
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Serialize filters for comparison
  const filtersKey = useMemo(
    () => JSON.stringify({ fieldGroupId, ...filters }),
    [fieldGroupId, filters],
  );

  const fetchInstances = useCallback(
    async (reset: boolean) => {
      if (!fieldGroupId || isLoadingRef.current) return;

      isLoadingRef.current = true;
      const currentOffset = reset ? 0 : offsetRef.current;
      const currentFilters = filtersRef.current;

      try {
        setLoading(true);

        const queryParams = new URLSearchParams({
          limit: LIMIT.toString(),
          offset: currentOffset.toString(),
          group_id: fieldGroupId,
        });

        if (currentFilters.searchTerm) {
          queryParams.append("search", currentFilters.searchTerm);
        }

        if (currentFilters.sortBy) {
          // Map frontend field names to backend (slug -> slugSuffix)
          const sortField =
            currentFilters.sortBy === "slug"
              ? "slug_suffix"
              : currentFilters.sortBy;
          queryParams.append("sort_by", sortField);
        }

        if (currentFilters.sortOrder) {
          queryParams.append("sort_order", currentFilters.sortOrder);
        }

        const response = await fetchFastApi({
          path: `/controls/v2/field-group-instances?${queryParams.toString()}`,
          method: "GET",
        });

        const data = z
          .array(FieldGroupInstanceSchema)
          .parse(response.instances || []);
        // API returns total_count in snake_case
        const total = data.length ?? 0;
        setTotalCount(total);

        setInstances((prev) =>
          reset
            ? data
            : [
                ...prev,
                ...data.filter((i) => !prev.some((e) => e.id === i.id)),
              ],
        );

        setHasMore(data.length === LIMIT);

        const newOffset = reset ? LIMIT : currentOffset + LIMIT;
        offsetRef.current = newOffset;
      } catch (error) {
        console.error("Failed to fetch field group instances:", error);
        toast.error("Failed to fetch library items");
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    },
    [fieldGroupId],
  );

  // Initial fetch & refetch when filters change
  useEffect(() => {
    if (!fieldGroupId) {
      setInstances([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    offsetRef.current = 0;
    setHasMore(true);
    setInstances([]);
    fetchInstances(true);
  }, [filtersKey, fetchInstances, fieldGroupId]);

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;
    fetchInstances(false);
  }, [hasMore, fetchInstances]);

  return {
    instances,
    totalCount,
    loading,
    loadMore,
    hasMore,
    refetch: () => fetchInstances(true),
  };
};

// Hook for creating a field group instance
export const useCreateFieldGroupInstance = () => {
  const [loading, setLoading] = useState(false);

  const createInstance = useCallback(
    async (
      data: CreateFieldGroupInstanceRequest,
    ): Promise<FieldGroupInstance | null> => {
      setLoading(true);
      try {
        // Build body with optional slugSuffix
        const body: Record<string, string | number | boolean | object | null> =
          {
            group_id: data.groupId,
            name: data.name,
            description: data.description ?? null,
            field_values: data.fieldValues ?? null,
          };
        // Only include slug_suffix if provided (otherwise auto-generated)
        if (data.slugSuffix) {
          body.slug_suffix = data.slugSuffix;
        }

        const response = await fetchFastApi({
          path: `/controls/v2/field-group-instances`,
          method: "POST",
          body,
        });

        toast.success("Item created successfully");
        return FieldGroupInstanceSchema.parse(response.instance);
      } catch (error) {
        console.error("Failed to create field group instance:", error);
        toast.error("Failed to create item");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createInstance,
    loading,
  };
};

// Hook for updating a field group instance
export const useUpdateFieldGroupInstance = () => {
  const [loading, setLoading] = useState(false);

  const updateInstance = useCallback(
    async (
      instanceId: string,
      data: UpdateFieldGroupInstanceRequest,
    ): Promise<FieldGroupInstance | null> => {
      setLoading(true);
      try {
        // Build body with only provided fields
        const body: Record<string, string | number | boolean | object | null> =
          {};
        if (data.slugSuffix !== undefined) {
          body.slug_suffix = data.slugSuffix;
        }
        if (data.name !== undefined) {
          body.name = data.name;
        }
        if (data.description !== undefined) {
          body.description = data.description;
        }
        if (data.fieldValues !== undefined) {
          body.field_values = data.fieldValues;
        }

        const response = await fetchFastApi({
          path: `/controls/v2/field-group-instances/${instanceId}`,
          method: "PATCH",
          body,
        });

        toast.success("Item updated successfully");
        return FieldGroupInstanceSchema.parse(response.instance);
      } catch (error) {
        console.error("Failed to update field group instance:", error);
        toast.error("Failed to update item");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateInstance,
    loading,
  };
};

// Hook for deleting a field group instance
export const useDeleteFieldGroupInstance = () => {
  const [loading, setLoading] = useState(false);

  const deleteInstance = useCallback(
    async (instanceId: string): Promise<boolean> => {
      setLoading(true);
      try {
        await fetchFastApi({
          path: `/controls/v2/field-group-instances/${instanceId}`,
          method: "DELETE",
        });

        toast.success("Item deleted successfully");
        return true;
      } catch (error) {
        console.error("Failed to delete field group instance:", error);
        toast.error("Failed to delete item");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    deleteInstance,
    loading,
  };
};

// Hook for fetching a single field group by ID
export const useFieldGroup = (fieldGroupId: string | null) => {
  const [fieldGroup, setFieldGroup] = useState<FieldGroup | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchFieldGroup = useCallback(async () => {
    if (!fieldGroupId) {
      setFieldGroup(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetchFastApi({
        path: `/controls/v2/field-groups/${fieldGroupId}`,
        method: "GET",
      });

      setFieldGroup(FieldGroupSchema.parse(response.fieldGroup));
    } catch (error) {
      console.error("Failed to fetch field group:", error);
      toast.error("Failed to fetch library");
      setFieldGroup(null);
    } finally {
      setLoading(false);
    }
  }, [fieldGroupId]);

  useEffect(() => {
    fetchFieldGroup();
  }, [fetchFieldGroup]);

  return {
    fieldGroup,
    loading,
    refetch: fetchFieldGroup,
  };
};

// Hook for fetching a single hydrated field group instance
export const useFieldGroupInstance = (instanceId: string | null) => {
  const [instance, setInstance] = useState<HydratedFieldGroupInstance | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const fetchInstance = useCallback(async () => {
    if (!instanceId) {
      setInstance(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetchFastApi({
        path: `/controls/v2/field-group-instances/${instanceId}`,
        method: "GET",
      });

      setInstance(HydratedFieldGroupInstanceSchema.parse(response.instance));
    } catch (error) {
      console.error("Failed to fetch field group instance:", error);
      setInstance(null);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    fetchInstance();
  }, [fetchInstance]);

  return {
    instance,
    loading,
    refetch: fetchInstance,
  };
};

// Hook for creating a new field group (library type)
export const useCreateFieldGroup = () => {
  const [loading, setLoading] = useState(false);

  const createFieldGroup = useCallback(
    async (data: CreateFieldGroupRequest): Promise<FieldGroup | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: "/controls/v2/field-groups",
          method: "POST",
          body: {
            name: data.name,
            slug_prefix: data.slugPrefix, // Required
            description: data.description ?? null,
            display_order: data.displayOrder ?? 0,
          },
        });

        toast.success("Library created successfully");
        return FieldGroupSchema.parse(response.fieldGroup);
      } catch (error) {
        console.error("Failed to create field group:", error);
        toast.error("Failed to create library");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createFieldGroup,
    loading,
  };
};

// Hook for updating a field group (library type)
export const useUpdateFieldGroup = () => {
  const [loading, setLoading] = useState(false);

  const updateFieldGroup = useCallback(
    async (
      groupId: string,
      data: UpdateFieldGroupRequest,
    ): Promise<FieldGroup | null> => {
      setLoading(true);
      try {
        const body: Record<string, string | number | boolean | object | null> =
          {};
        if (data.name !== undefined) body.name = data.name;
        if (data.slugPrefix !== undefined) body.slug_prefix = data.slugPrefix;
        if (data.description !== undefined) body.description = data.description;
        if (data.displayOrder !== undefined)
          body.display_order = data.displayOrder;

        const response = await fetchFastApi({
          path: `/controls/v2/field-groups/${groupId}`,
          method: "PATCH",
          body,
        });

        toast.success("Library updated successfully");
        return FieldGroupSchema.parse(response.fieldGroup);
      } catch (error) {
        console.error("Failed to update field group:", error);
        toast.error("Failed to update library");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateFieldGroup,
    loading,
  };
};

// Hook for deleting a field group (library type)
export const useDeleteFieldGroup = () => {
  const [loading, setLoading] = useState(false);

  const deleteFieldGroup = useCallback(
    async (groupId: string): Promise<boolean> => {
      setLoading(true);
      try {
        await fetchFastApi({
          path: `/controls/v2/field-groups/${groupId}`,
          method: "DELETE",
        });

        toast.success("Library deleted successfully");
        return true;
      } catch (error) {
        console.error("Failed to delete field group:", error);
        toast.error("Failed to delete library");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    deleteFieldGroup,
    loading,
  };
};

// Hook for fetching control relationships
export const useControlRelationships = (controlId: string | null) => {
  const [relationships, setRelationships] = useState<ControlRelationship[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRelationships = useCallback(async () => {
    if (!controlId) {
      setRelationships([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetchFastApi({
        path: `/controls/v2/control/${controlId}/relationships`,
        method: "GET",
      });

      // API returns { instances: [...], availableGroups: [...] }
      const data = z
        .array(ControlRelationshipSchema)
        .parse(response.instances || []);
      setRelationships(data);
    } catch (error) {
      console.error("Failed to fetch control relationships:", error);
      // Don't show toast for initial fetch - just set empty array
      setRelationships([]);
    } finally {
      setLoading(false);
    }
  }, [controlId]);

  useEffect(() => {
    fetchRelationships();
  }, [fetchRelationships]);

  return {
    relationships,
    loading,
    refetch: fetchRelationships,
  };
};

// Hook for linking an existing instance to a control
export const useLinkInstance = () => {
  const [loading, setLoading] = useState(false);

  const linkInstance = useCallback(
    async (
      controlId: string,
      instanceId: string,
    ): Promise<ControlRelationship | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/controls/v2/control/${controlId}/relationships/link`,
          method: "POST",
          body: {
            instance_id: instanceId,
          },
        });

        // API returns { link: {...} }
        return ControlRelationshipSchema.parse(response.link);
      } catch (error) {
        console.error("Failed to link instance to control:", error);
        toast.error("Failed to add relationship");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    linkInstance,
    loading,
  };
};

// Hook for unlinking an instance from a control
export const useUnlinkInstance = () => {
  const [loading, setLoading] = useState(false);

  const unlinkInstance = useCallback(
    async (controlId: string, linkId: string): Promise<boolean> => {
      setLoading(true);
      try {
        await fetchFastApi({
          path: `/controls/v2/control/${controlId}/relationships/${linkId}`,
          method: "DELETE",
        });

        return true;
      } catch (error) {
        console.error("Failed to unlink instance from control:", error);
        toast.error("Failed to remove relationship");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    unlinkInstance,
    loading,
  };
};

// Hook for searching field group instances across all field groups (for relationship picker)
// Returns hydrated instances with computed slug and group info
export const useSearchFieldGroupInstances = () => {
  const [instances, setInstances] = useState<FieldGroupInstance[]>([]);
  const [loading, setLoading] = useState(false);

  const searchInstances = useCallback(
    async (params: {
      searchTerm?: string;
      fieldGroupId?: string | null;
      excludeControlId?: string;
      limit?: number;
    }) => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          limit: (params.limit ?? 25).toString(),
        });

        if (params.searchTerm) {
          queryParams.append("search", params.searchTerm);
        }

        if (params.fieldGroupId) {
          queryParams.append("group_id", params.fieldGroupId);
        }

        if (params.excludeControlId) {
          queryParams.append("exclude_control_id", params.excludeControlId);
        }

        const response = await fetchFastApi({
          path: `/controls/v2/field-group-instances?${queryParams.toString()}`,
          method: "GET",
        });

        // Parse response - API returns hydrated instances
        const rawInstances = response.instances || [];
        const data = z.array(FieldGroupInstanceSchema).parse(rawInstances);
        setInstances(data);
        return data;
      } catch (error) {
        console.error("Failed to search field group instances:", error);
        setInstances([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    instances,
    loading,
    searchInstances,
  };
};

// ============================================
// Field Definition Hooks
// ============================================

export interface CreateFieldDefinitionRequest {
  name: string;
  description?: string | null;
  fieldType?: "text" | "user" | "date";
  fieldConfig?: Record<string, unknown>;
  isRequired?: boolean;
  displayOrder?: number;
}

export interface UpdateFieldDefinitionRequest {
  name?: string;
  description?: string | null;
  fieldConfig?: Record<string, unknown>;
  isRequired?: boolean;
  displayOrder?: number;
}

// Hook for creating a field definition
export const useCreateFieldDefinition = () => {
  const [loading, setLoading] = useState(false);

  const createFieldDefinition = useCallback(
    async (
      groupId: string,
      data: CreateFieldDefinitionRequest,
    ): Promise<boolean> => {
      setLoading(true);
      try {
        await fetchFastApi({
          path: `/controls/v2/field-groups/${groupId}/fields`,
          method: "POST",
          body: {
            name: data.name,
            description: data.description ?? null,
            field_type: data.fieldType ?? "text",
            field_config: data.fieldConfig ?? {},
            is_required: data.isRequired ?? false,
            display_order: data.displayOrder ?? 0,
          },
        });

        toast.success("Field created successfully");
        return true;
      } catch (error) {
        console.error("Failed to create field definition:", error);
        toast.error("Failed to create field");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createFieldDefinition,
    loading,
  };
};

// Hook for updating a field definition
export const useUpdateFieldDefinition = () => {
  const [loading, setLoading] = useState(false);

  const updateFieldDefinition = useCallback(
    async (
      groupId: string,
      fieldId: string,
      data: UpdateFieldDefinitionRequest,
    ): Promise<boolean> => {
      setLoading(true);
      try {
        const body: Record<string, string | number | boolean | object | null> =
          {};
        if (data.name !== undefined) body.name = data.name;
        if (data.description !== undefined) body.description = data.description;
        if (data.fieldConfig !== undefined)
          body.field_config = data.fieldConfig;
        if (data.isRequired !== undefined) body.is_required = data.isRequired;
        if (data.displayOrder !== undefined)
          body.display_order = data.displayOrder;

        await fetchFastApi({
          path: `/controls/v2/field-groups/${groupId}/fields/${fieldId}`,
          method: "PATCH",
          body,
        });

        toast.success("Field updated successfully");
        return true;
      } catch (error) {
        console.error("Failed to update field definition:", error);
        toast.error("Failed to update field");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateFieldDefinition,
    loading,
  };
};

// Hook for deleting a field definition
export const useDeleteFieldDefinition = () => {
  const [loading, setLoading] = useState(false);

  const deleteFieldDefinition = useCallback(
    async (groupId: string, fieldId: string): Promise<boolean> => {
      setLoading(true);
      try {
        await fetchFastApi({
          path: `/controls/v2/field-groups/${groupId}/fields/${fieldId}`,
          method: "DELETE",
        });

        toast.success("Field deleted successfully");
        return true;
      } catch (error) {
        console.error("Failed to delete field definition:", error);
        toast.error("Failed to delete field");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    deleteFieldDefinition,
    loading,
  };
};
